import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Queue as QueueType } from "../types.ts";

/**
 * Queue — ordered list of items with drag-to-reorder, click-to-jump, and
 * per-item remove. Uses @dnd-kit for keyboard-accessible drag.
 *
 * The current item is highlighted. Clicking an item becomes the new
 * current item (FR-017). Dragging reorders (FR-015). The × button
 * removes (FR-016).
 */
export interface QueueProps {
  queue: QueueType;
  onJumpTo: (index: number) => void;
  onRemove: (index: number) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
}

interface RowProps {
  id: string;
  index: number;
  title: string;
  sub: string;
  thumb: string;
  current: boolean;
  onClick: () => void;
  onRemove: () => void;
}

function Row({ id, title, sub, thumb, current, onClick, onRemove }: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`queue-item${current ? " current" : ""}`}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <img src={thumb} alt="" />
      <div className="meta">
        <div className="title">{title}</div>
        {sub && <div className="sub">{sub}</div>}
      </div>
      <button
        className="remove"
        aria-label={`Remove ${title} from queue`}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        ×
      </button>
    </div>
  );
}

export function Queue({ queue, onJumpTo, onRemove, onReorder }: QueueProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = queue.items.findIndex((i) => i.identifier === active.id);
    const to = queue.items.findIndex((i) => i.identifier === over.id);
    if (from === -1 || to === -1) return;
    onReorder(from, to);
  }

  return (
    <div className="queue">
      <h2>{queue.name}</h2>
      {queue.items.length === 0 ? (
        <p className="paste-box-hint" style={{ padding: "0.5rem" }}>
          Paste a URL to begin.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={queue.items.map((i) => i.identifier)}
            strategy={verticalListSortingStrategy}
          >
            {queue.items.map((item, idx) => (
              <Row
                key={item.identifier}
                id={item.identifier}
                index={idx}
                title={item.title}
                sub={[item.year, item.uploader].filter(Boolean).join(" · ")}
                thumb={item.thumbUrl}
                current={idx === queue.currentIndex}
                onClick={() => onJumpTo(idx)}
                onRemove={() => onRemove(idx)}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
